import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const EMPTY_SCHEDULE = { startTime: "", endTime: "" };

// Categories used to be a hardcoded list shared (and duplicated) across
// both frontends - the only way to hide "late_night" during an off
// season, or add a new one, was to edit code and redeploy both apps.
// This is the real management UI for it, including scheduling a category
// to specific hours (Bangladesh time) instead of just on/off.
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [busySlug, setBusySlug] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [schedule, setSchedule] = useState(EMPTY_SCHEDULE);
  const [scheduleError, setScheduleError] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const token = localStorage.getItem("authToken");

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/category?all=true`);
      setCategories(response.data.categories);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Couldn't load categories.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE_URL}/category`,
        { name },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setName("");
      await fetchCategories();
    } catch (err) {
      console.error("Error creating category:", err);
      setError(err.response?.data?.message || "Failed to create category.");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleVisible = async (category) => {
    setBusySlug(category.slug);
    try {
      await axios.patch(
        `${API_BASE_URL}/category/${category._id}`,
        { visible: !category.visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCategories();
    } catch (err) {
      console.error("Error updating category:", err);
      alert(err.response?.data?.message || "Failed to update category.");
    } finally {
      setBusySlug(null);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete "${category.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API_BASE_URL}/category/${category._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchCategories();
    } catch (err) {
      console.error("Error deleting category:", err);
      alert(err.response?.data?.message || "Failed to delete category.");
    }
  };

  const startEditSchedule = (category) => {
    setScheduleError(null);
    setEditingId(category._id);
    setSchedule({ startTime: category.startTime || "", endTime: category.endTime || "" });
  };

  const cancelEditSchedule = () => {
    setEditingId(null);
    setSchedule(EMPTY_SCHEDULE);
    setScheduleError(null);
  };

  const saveSchedule = async (category) => {
    setScheduleError(null);
    setSavingSchedule(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/category/${category._id}`,
        { startTime: schedule.startTime, endTime: schedule.endTime },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditingId(null);
      await fetchCategories();
    } catch (err) {
      console.error("Error saving schedule:", err);
      setScheduleError(err.response?.data?.message || "Failed to save schedule.");
    } finally {
      setSavingSchedule(false);
    }
  };

  const clearSchedule = async (category) => {
    setBusySlug(category.slug);
    try {
      await axios.patch(
        `${API_BASE_URL}/category/${category._id}`,
        { startTime: "", endTime: "" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchCategories();
    } catch (err) {
      console.error("Error clearing schedule:", err);
      alert(err.response?.data?.message || "Failed to clear schedule.");
    } finally {
      setBusySlug(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading categories…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Categories</h2>
      <p className="text-sm text-sub mb-6 max-w-xl">
        Hidden categories stay on any item that already has a price for them (nothing is
        lost) - they just stop showing as a storefront tab. A schedule (Bangladesh time)
        makes a category show as "closed right now" outside its hours instead - leave both
        times blank for a category that's always available whenever it's visible.
      </p>

      <div className="bg-card border border-line rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-paper text-left text-xs uppercase tracking-wide text-muted border-b border-line">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Status</th>
              <th className="p-3">Schedule (Bangladesh time)</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {categories.map((category) => {
              const busy = busySlug === category.slug;
              const isEditing = editingId === category._id;
              const hasSchedule = category.startTime && category.endTime;
              return (
                <React.Fragment key={category._id}>
                  <tr className="hover:bg-paper/60 transition">
                    <td className="p-3 text-ink">{category.name}</td>
                    <td className="p-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          category.visible
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {category.visible ? "Visible" : "Hidden"}
                      </span>
                      {category.visible && hasSchedule && (
                        <span
                          className={`ml-1.5 text-xs px-2 py-0.5 rounded-full ${
                            category.isOpenNow
                              ? "bg-green-50 text-green-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {category.isOpenNow ? "Open now" : "Closed now"}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-sub">
                      {hasSchedule ? `${category.startTime}–${category.endTime}` : "Always"}
                    </td>
                    <td className="p-3 space-x-3 whitespace-nowrap">
                      <button
                        onClick={() => toggleVisible(category)}
                        disabled={busy}
                        className="text-primary text-xs font-semibold disabled:opacity-50"
                      >
                        {category.visible ? "Hide" : "Show"}
                      </button>
                      <button
                        onClick={() => (isEditing ? cancelEditSchedule() : startEditSchedule(category))}
                        className="text-primary text-xs font-semibold"
                      >
                        {isEditing ? "Cancel" : "Set Schedule"}
                      </button>
                      {hasSchedule && (
                        <button
                          onClick={() => clearSchedule(category)}
                          disabled={busy}
                          className="text-xs font-semibold text-ink disabled:opacity-50"
                        >
                          Clear Schedule
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(category)}
                        className="text-xs font-semibold text-red-600"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                  {isEditing && (
                    <tr className="bg-paper/40">
                      <td colSpan={4} className="p-4">
                        {scheduleError && (
                          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2 mb-3 max-w-lg">
                            {scheduleError}
                          </p>
                        )}
                        <div className="flex flex-wrap items-end gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-ink mb-1">
                              Start time
                            </label>
                            <input
                              type="time"
                              value={schedule.startTime}
                              onChange={(e) => setSchedule({ ...schedule, startTime: e.target.value })}
                              className="p-2 border border-line rounded"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-ink mb-1">
                              End time
                            </label>
                            <input
                              type="time"
                              value={schedule.endTime}
                              onChange={(e) => setSchedule({ ...schedule, endTime: e.target.value })}
                              className="p-2 border border-line rounded"
                            />
                          </div>
                          <button
                            onClick={() => saveSchedule(category)}
                            disabled={savingSchedule}
                            className="bg-primary text-white py-2 px-4 rounded hover:opacity-90 transition disabled:opacity-60"
                          >
                            {savingSchedule ? "Saving…" : "Save Schedule"}
                          </button>
                        </div>
                        <p className="text-xs text-sub mt-2">
                          An end time earlier than the start time (e.g. 22:00–02:00) is
                          treated as spanning midnight.
                        </p>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <form
        onSubmit={handleCreate}
        className="bg-card border border-line rounded-lg p-6 space-y-4 mt-6 max-w-md"
      >
        <h3 className="text-lg font-semibold text-ink">Add a category</h3>
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </p>
        )}
        <input
          placeholder="Name (e.g. Weekend Special)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full p-2 border border-line rounded"
        />
        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-60"
        >
          {submitting ? "Adding…" : "Add Category"}
        </button>
      </form>
    </div>
  );
};

export default Categories;
